import { functions, firestore } from "utils/firebase";

// Get all retention metrics
export const getRetentionMetrics = async (filter = "monthly") => {
	try {
		const getRetentionMetrics = functions.httpsCallable("getRetentionMetrics");
		const result = await getRetentionMetrics({ filter });
		return result.data;
	} catch (error) {
		console.error("Error fetching retention metrics:", error);
		throw error;
	}
};

// Get cohort data
export const getCohortData = async (limit = 12, filter = "monthly") => {
	try {
		const getCohortData = functions.httpsCallable("getCohortData");
		const result = await getCohortData({ count: limit, filter });
		return result.data.cohortData;
	} catch (error) {
		console.error("Error fetching cohort data:", error);
		throw error;
	}
};

// Get daily snapshots
export const getDailySnapshots = async (days = 30) => {
	try {
		const getDailySnapshots = functions.httpsCallable("getDailySnapshots");
		const result = await getDailySnapshots({ days });
		return result.data.snapshots;
	} catch (error) {
		console.error("Error fetching daily snapshots:", error);
		throw error;
	}
};

// Get engagement metrics
export const getEngagementMetrics = async (filter = "monthly") => {
	try {
		const getEngagementMetrics = functions.httpsCallable(
			"getEngagementMetrics",
		);
		const result = await getEngagementMetrics({ filter });
		// The data is directly in result.data, not nested under metrics
		return result.data;
	} catch (error) {
		console.error("Error fetching engagement metrics:", error);
		throw error;
	}
};

// Calculate all retention data
export const calculateAllRetentionData = async () => {
	try {
		const calculateAllRetentionData = functions.httpsCallable(
			"calculateAllRetentionData",
		);
		const result = await calculateAllRetentionData();
		return result.data;
	} catch (error) {
		console.error("Error calculating retention data:", error);
		throw error;
	}
};

// Build historical snapshots for the last 6 months
export const buildHistoricalSnapshots = async () => {
	try {
		const buildHistoricalSnapshots = functions.httpsCallable(
			"buildHistoricalSnapshots",
		);
		const result = await buildHistoricalSnapshots();
		return result.data;
	} catch (error) {
		console.error("Error building historical snapshots:", error);
		throw error;
	}
};

// Get live metrics (real-time data)
export const getLiveMetrics = async () => {
	try {
		const getLiveMetrics = functions.httpsCallable("getLiveMetrics");
		const result = await getLiveMetrics();
		return result.data;
	} catch (error) {
		console.error("Error fetching live metrics:", error);
		throw error;
	}
};

// Get build progress for historical snapshots
export const getBuildProgress = async () => {
	try {
		const progressDoc = await firestore
			.collection("analytics")
			.doc("buildProgress")
			.get();

		if (!progressDoc.exists) {
			return null;
		}

		return progressDoc.data();
	} catch (error) {
		console.error("Error getting build progress:", error);
		throw error;
	}
};

// Get at-risk users with optional filtering
export const getAtRiskUsers = async (
	riskType = null,
	limit = 100,
	filter = "monthly",
) => {
	try {
		const getAtRiskUsers = functions.httpsCallable("getAtRiskUsers");
		const result = await getAtRiskUsers({ riskType, limit, filter });
		return result.data;
	} catch (error) {
		console.error("Error fetching at-risk users:", error);
		throw error;
	}
};

// Get retention suggestions based on metrics
export const getRetentionSuggestions = async (metrics) => {
	const suggestions = [];

	if (!metrics) return suggestions;

	// Day 1 retention suggestions
	if (metrics.dailyMetrics?.dauMauRatio < 0.3) {
		suggestions.push({
			type: "engagement",
			priority: "high",
			title: "Low Daily Engagement",
			description:
				"Consider implementing daily push notifications or streak rewards to improve DAU/MAU ratio",
			metric: "DAU/MAU Ratio",
			value: metrics.dailyMetrics?.dauMauRatio,
			target: "> 0.3",
		});
	}

	// Verification rate suggestions
	if (metrics.engagementMetrics?.verificationRate < 0.8) {
		suggestions.push({
			type: "onboarding",
			priority: "medium",
			title: "Improve Verification Flow",
			description:
				"Streamline the verification process to increase completion rate",
			metric: "Verification Rate",
			value: `${(metrics.engagementMetrics.verificationRate * 100).toFixed(
				1,
			)}%`,
			target: "> 80%",
		});
	}

	// First match time suggestions
	if (metrics.engagementMetrics?.avgDaysToFirstMatch > 3) {
		suggestions.push({
			type: "matching",
			priority: "high",
			title: "Slow First Match Experience",
			description:
				"Consider widening match criteria or providing boost for new users",
			metric: "Avg Days to First Match",
			value: `${metrics.engagementMetrics.avgDaysToFirstMatch} days`,
			target: "< 3 days",
		});
	}

	// At-risk users suggestions
	if (metrics.atRiskSummary?.inactiveCount > 100) {
		suggestions.push({
			type: "retention",
			priority: "high",
			title: "High Inactivity Rate",
			description: "Launch re-engagement campaign for inactive users",
			metric: "Inactive Users",
			value: metrics.atRiskSummary.inactiveCount,
			target: "< 100",
		});
	}

	return suggestions;
};

// Export at-risk users to CSV
export const exportAtRiskUsers = async (
	riskType = null,
	filter = "6months",
) => {
	try {
		const response = await getAtRiskUsers(riskType, 1000, filter);
		const users = response.users || response;

		const csv = [
			[
				"User ID",
				"Name",
				"Email",
				"Risk Type",
				"Risk Reason",
				"Last Login",
				"Created At",
				"Messages",
				"Matches",
				"Verified",
			],
			...users.map((user) => [
				user.userId,
				user.name || "",
				user.email || "",
				user.riskType || "",
				user.riskReason || "",
				user.lastLogin
					? new Date(user.lastLogin).toLocaleDateString()
					: "Never",
				user.createdAt
					? new Date(user.createdAt).toLocaleDateString()
					: "Unknown",
				user.messageCount || 0,
				user.matchCount || 0,
				user.verified ? "Yes" : "No",
			]),
		]
			.map((row) => row.join(","))
			.join("\n");

		const blob = new Blob([csv], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `at-risk-users-${riskType || "all"}-${filter}-${
			new Date().toISOString().split("T")[0]
		}.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Error exporting at-risk users:", error);
		throw error;
	}
};

// Recompute company counts from scratch
export const recomputeCompanyCounts = async () => {
	try {
		const recomputeCompanyCounts = functions.httpsCallable(
			"recomputeCompanyCounts",
		);
		const result = await recomputeCompanyCounts();
		return result.data;
	} catch (error) {
		console.error("Error recomputing company counts:", error);
		throw error;
	}
};
